import { useEffect, useRef, useState } from 'react';
import { useOrderDetails } from 'components/context/order_details';
import { getEnvironment } from 'utilities/environment';
import { localizedShopPath, getLocaleString } from 'utilities/locales';
import getUuidByString from 'uuid-by-string';
import { PAY_WITH_CASH, isFeatureEnabled } from 'utilities/features';
import { CARD_INTENT } from 'constants/enums';

const isProduction = getEnvironment().env === 'production';

const createPaymentRequest = (orderData, storeCode) => ({
  requestShippingAddress: false,
  requestBillingInfo: true,
  currencyCode: orderData.payment_due.currency,
  countryCode: storeCode,
  total: {
    label: 'SQUARE SHOP',
    amount: orderData.payment_due.amount,
    pending: false,
  },
  lineItems: [
    {
      label: 'Subtotal',
      amount: orderData.payment_due.amount,
      pending: false,
    },
  ],
});

const createVerificationDetails = (orderData, cardIntent) => {
  const billingAddress = orderData.billing_address;
  const contactInformation = orderData.contact_information;
  return {
    intent: cardIntent,
    squareProduct: 'MARKET',
    billingContact: {
      familyName: billingAddress.last_name,
      givenName: billingAddress.first_name,
      email: contactInformation.email,
      country: billingAddress.country_code,
      city: billingAddress.locality,
      addressLines: [
        billingAddress.address_line_1,
        billingAddress.address_line_2 || '',
      ],
      postalCode: billingAddress.postal_code,
      phone: contactInformation.phone,
      state: billingAddress.administrative_district_level_1,
    },
    ...([CARD_INTENT.CHARGE, CARD_INTENT.CHARGE_AND_STORE].includes(cardIntent)
      ? {
          amount: orderData.payment_due.amount,
          currencyCode: orderData.payment_due.currency,
        }
      : {}),
  };
};

const initGooglePay = async (payments, paymentRequest) => {
  try {
    return await payments.googlePay(payments.paymentRequest(paymentRequest));
  } catch {
    // if it fails it just means Google Pay is not supported
    return null;
  }
};

const initApplePay = async (payments, paymentRequest) => {
  try {
    return await payments.applePay(payments.paymentRequest(paymentRequest));
  } catch {
    // if it fails it just means Apple Pay is not supported
    return null;
  }
};

const cashAppPayParams = (redirectPath, referenceId) => {
  const redirectURL = `${window.location.origin}${redirectPath}`;

  return {
    redirectURL,
    referenceId,
  };
};

const initCashAppPay = async (payments, paymentRequest, params) => {
  try {
    return await payments.cashAppPay(
      payments.paymentRequest(paymentRequest),
      params,
    );
  } catch {
    return null;
  }
};

const attachCashAppPay = (cashAppPay) => {
  const options = {
    shape: 'semiround',
    width: 'full',
  };

  cashAppPay.attach('#pay-with-cash-app', options);
};

const usePaymentSdk = () => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  useEffect(() => {
    const script = document.createElement('script');

    script.src = isProduction
      ? 'https://web.squarecdn.com/v1/square.js'
      : 'https://staging.web.squarecdn.com/v1/square.js';
    script.async = true;
    script.onload = () => setScriptLoaded(true);

    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      setScriptLoaded(false);
    };
  }, []);

  return scriptLoaded;
};

const usePaymentForm = (scriptLoaded, orderData) => {
  const { number, store, appID, locationID } = useOrderDetails();

  const sqPayments = useRef(null);

  // Payment Methods
  const [sqCard, setSqCard] = useState(null);
  const [googlePay, setGooglePay] = useState(null);
  const [applePay, setApplePay] = useState(null);
  const [cashAppPay, setCashAppPay] = useState(null);

  useEffect(() => {
    if (!scriptLoaded || Object.entries(orderData).length === 0) {
      return;
    }

    const payments = window.Square.payments(appID, locationID);
    sqPayments.current = payments;
    payments.setLocale(getLocaleString());

    const cardPromise = payments.card();
    let applePayPromise, cashAppPayPromise, googlePayPromise;

    if (
      !orderData.upfront_card_payment_only &&
      !orderData.first_party_payment_only &&
      !orderData.is_hardware_rental_order &&
      orderData.payment_due.payment_schedule !== 'installment'
    ) {
      const paymentRequest = createPaymentRequest(orderData, store);
      googlePayPromise = initGooglePay(payments, paymentRequest);
      applePayPromise = initApplePay(payments, paymentRequest);

      if (isFeatureEnabled(PAY_WITH_CASH)) {
        const cashAppPaymentRequest = createPaymentRequest(orderData, store);

        const params = cashAppPayParams(
          `${localizedShopPath()}/checkout/v2/cash_app_pay`,
          getUuidByString(number + orderData.total.amount),
        );

        cashAppPayPromise = initCashAppPay(
          payments,
          cashAppPaymentRequest,
          params,
        );
      }
    }

    cardPromise.then((result) => {
      setSqCard(result);
      result.attach('#empty-div-for-payment-form');
    });

    const addDigitalWallet = (setter) => (wallet) => {
      if (wallet) setter(wallet);
    };
    googlePayPromise?.then(addDigitalWallet(setGooglePay));
    applePayPromise?.then(addDigitalWallet(setApplePay));
    cashAppPayPromise?.then(addDigitalWallet(setCashAppPay));
  }, [scriptLoaded, orderData]);

  return {
    sqPayments,
    sqCard,
    googlePay,
    applePay,
    cashAppPay,
  };
};

export {
  createPaymentRequest,
  createVerificationDetails,
  initGooglePay,
  initApplePay,
  initCashAppPay,
  cashAppPayParams,
  attachCashAppPay,
  usePaymentSdk,
  usePaymentForm,
};
